import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../constants/actionTypes';
import { actions as locationDashboardActions } from '../../actions/locationDashboardActions';
import { LOCATION_DASHBOARD_URL_PARAMS as URLParams  } from '../../constants/constants';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import Spreadsheet from '../Common/Spreadsheet/Spreadsheet';
import './LocationFinancialsSpreadsheet.scss';
import Button from '../Common/Button';
import Notification from '../Common/Notification';
import Routes from '../../constants/routes';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';

class LocationFinancialsSpreadsheet extends React.Component{
    constructor(props, context) {
        super(props, context)
        this.spreadsheet = React.createRef();
        this.state = {
            locationTasks: [],
            allowTaskBudgetEntry: false,
            isSubscriptionFinancialsActive: false,
            isLoading: false,
            spreadsheetHeader:[]
        }
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    /**
     * Wrapper for retrieving the selected location from Redux.  We're now
     * storing it there instead of in local component state, because it has to
     * share that selected location state with the Task List.
     * 
     * This function also encapsulates the logic to override the normal operation
     * of sharing the redux selected location, by instead selecting the location
     * passed to this page via it's URL parameters.
     */
    getSelectedLocationKey = () => {
        if (this.props.urlParams && this.props.urlParams.locationKey) {
            return this.props.urlParams.locationKey.toString();
        } else {
            const reduxKey = (this.props.taskListState
                && this.props.taskListState.searchParams
                && this.props.taskListState.searchParams.locationKeys
                && this.props.taskListState.searchParams.locationKeys.length > 0) ? this.props.taskListState.searchParams.locationKeys[0].toString() : '';

            return reduxKey;
        }
    }
 
    loadData = async () => {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.locationDashboardActions.loadFinancialsSpreadsheet(this.getSelectedLocationKey());
            if (response.type === types.LOAD_LOCATION_FINANCIALS_SPREADSHEET_SUCCESS) {
                this.setState({
                    locationTasks: response.payload.listTasks,
                    allowTaskBudgetEntry: response.payload.allowTaskBudgetEntry,
                    isSubscriptionFinancialsActive : response.payload.isSubscriptionFinancialsActive,
                    spreadsheetHeader: response.payload.listTasksHeader
                }, () => {
                    if (this.state.isSubscriptionFinancialsActive){
                        this.loadDataTable();
                        this.spreadsheet.current.spreadsheet.events.on("afterEditEnd", (cell, value) => {
                            const valueToNum = Number(value) !== 0 ? (Number(value)|| "") : 0;
                            if (!Number.isInteger(valueToNum)){
                                this.spreadsheet.current.spreadsheet.setValue(cell, (valueToNum ? Math.round(valueToNum) : valueToNum));
                            }
                        });
                    }
                });
            } else if (response.type === types.LOAD_LOCATION_FINANCIALS_SPREADSHEET_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadDataTable = () => {
        const spreadsheet = this.spreadsheet.current.spreadsheet;

        spreadsheet.contextMenu.data.remove(["lock", "clear", "columns", "rows"]);

        // Lock specified header columns
        for (let item of this.state.spreadsheetHeader) {
            if (item.isLocked) {
                spreadsheet.lock(item.cell + ":" + item.cell[0] + (this.state.locationTasks.length + 1).toString());
            }
        }

        // Build header row (rowIndex = 1)
        const headerRow = this.state.spreadsheetHeader.map(header => ({
            cell: header.cell,
            value: header.value,
            isLocked: header.isLocked,
            format: header.format,
            css: header.css,
            propName: header.propName
        }));

        // Build data rows starting from rowIndex = 2
        const dataRows = this.state.locationTasks.map((task, index) => {
            const rowIndex = index + 2;
            return this.state.spreadsheetHeader.map(header => ({
                cell: header.cell[0] + rowIndex, // e.g., "A2", "B2"
                value: task.rowData[header.propName] ?? "",
                format: header.format ?? ""
            }));
        }).flat(); // flatten to a single array

        const dataTable = [...headerRow, ...dataRows];

        spreadsheet.parse(dataTable);
    }

    onSaveClick = async () => {

        this.spreadsheet.current.spreadsheet.endEdit();

        //only get the columns that need to be sent back
        let propColDict = {};
        for (let item of this.state.spreadsheetHeader){
            if (item.isSentBack){
                propColDict[item.cell[0]] = item.propName
            }
        }

        var savedSpreadSheetData = this.spreadsheet.current.spreadsheet.serialize().sheets[0].data;

        var groupedSpreadSheetData = savedSpreadSheetData.reduce((acc, { cell , value }) => {
            (acc[cell.slice(1)] = acc[cell.slice(1)] || []).push({ cell , value });
            return acc;
        }, []);

        groupedSpreadSheetData.splice(0,2);

        var savedTaskDataFromSheet = groupedSpreadSheetData.map( arr => {
            let savedTaskDetails = {};
            for (let obj of arr){
                const propColLetter = obj.cell[0];
                if (Object.keys(propColDict).includes(propColLetter)){
                    savedTaskDetails[propColDict[propColLetter]] = obj.value;
                }
            }
            return savedTaskDetails;
        })

        var fullLocationTasks = this.state.locationTasks.reduce((acc, { cell , value }) => {
            (acc[cell.slice(1)] = acc[cell.slice(1)] || []).push({ cell , value });
            return acc;
        }, []);

        var shortTaskArrayFromState = fullLocationTasks.map( arr => {
            let savedTaskDetails = {};
            for (let obj of arr){
                const propColLetter = obj.cell[0];
                if (Object.keys(propColDict).includes(propColLetter) && obj.value){
                    savedTaskDetails[propColDict[propColLetter]] = parseInt(obj.value);
                }
            }
            return savedTaskDetails;
        })

        const taskEqual = (oldTask, newTask) =>
            (Object.keys(oldTask).length === Object.keys(newTask).length 
            && Object.keys(oldTask).every(prop => oldTask[prop] === newTask[prop]));

        const updatedTasks = savedTaskDataFromSheet.filter( newTask => !shortTaskArrayFromState.some( origTask => taskEqual(origTask,newTask) ));

        if ( Array.isArray(updatedTasks) && updatedTasks.length ){
            try {
                const response = await this.props.locationDashboardActions.saveFinancialsSpreadsheet(updatedTasks);
                if (response.type === types.SAVE_LOCATION_FINANCIALS_SPREADSHEET_SUCCESS) {
                    this.loadData();
                    Notification("success", "Financial Data Updated", "");
                } else if (response.type === types.SAVE_LOCATION_FINANCIALS_SPREADSHEET_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                }
            } catch (error) {
                Notification("error", "Error", error);
            }
        }
        else{
            Notification("error", "Nothing to Update", "");
        }

    }

    onBackClick = () => {
        this.props.history.push(Routes.DashboardLocation);
    };

    render(){
        return(
            <LoadingIndicator isLoading={this.state.isLoading} isFullscreen={true} >
                <div className="financials-spreadsheet-container" style={{ height: (window.innerHeight - 125)+"px"}}>
                    {this.state.isSubscriptionFinancialsActive ?
                        <>
                            <Spreadsheet
                                colsCount={this.state.spreadsheetHeader.length}
                                rowsCount={this.state.locationTasks.length+1}
                                editLine={true}
                                menu={false}
                                multiSheets={false}
                                ref={this.spreadsheet} />
                            <div className="button-group">
                                <Button type="button" text="Save" bootstrapClass="primary" onClick={this.onSaveClick} />
                                <Button type="button" text="Back" bootstrapClass="secondary" onClick={this.onBackClick} />
                            </div>
                        </>
                        :
                        !this.state.isLoading &&
                        <div className="error-msg">This subscription does not have the task financials feature enabled.</div>
                    }
                </div>
            </LoadingIndicator>
        )
    }
}

LocationFinancialsSpreadsheet.propTypes = {
    locationDashboardActions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    taskListState: PropTypes.object.isRequired,
    user: PropTypes.object
};

function mapStateToProps(state, ownProps) {
    let urlParams = {
        locationKey: ""
    }
    const searchParams = new URLSearchParams(ownProps.location.search);
    if (searchParams.has(URLParams.locationKey)) {
        urlParams.locationKey = searchParams.get(URLParams.locationKey);
    }

    return {
        urlParams: urlParams,
        history: ownProps.history,
        taskListState: state.taskListState,
        user: state.user
    };
}

function mapDispatchToProps(dispatch) {
    return {
        locationDashboardActions: bindActionCreators(locationDashboardActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationFinancialsSpreadsheet);